/**
 * FL-246: ドライバースケジュール デモデータローダー
 *
 * デモ環境でのドライバースケジュールデータ投入・削除を管理
 */

import { getSupabaseClient } from '../../utils/supabase';
import logger from '../../utils/logger';
import { DEMO_DRIVER_SCHEDULES, DEMO_SCHEDULE_STATS } from './driverScheduleData';
import { DriverSchedule } from '../../types/schedule';

/**
 * デモドライバースケジュールをデータベースに投入
 */
export const loadDemoDriverSchedules = async (): Promise<{
  success: boolean;
  message: string;
  loadedCount?: number;
  error?: string;
}> => {
  try {
    logger.info('🔄 デモドライバースケジュール投入開始');

    const supabase = getSupabaseClient();
    if (!supabase) {
      throw new Error('Supabaseクライアントが初期化されていません');
    }

    // 既存データをクリア
    logger.info('🧹 既存スケジュール削除中...');
    const { error: deleteError } = await supabase.from('driver_schedules').delete().neq('id', ''); // すべて削除（ワイルドカード）

    if (deleteError && deleteError.code !== 'PGRST116') {
      // PGRST116: テーブルが空の場合のエラーは無視
      throw deleteError;
    }

    // デモデータを投入
    logger.info('📝 デモスケジュール投入中...', {
      count: DEMO_DRIVER_SCHEDULES.length,
    });

    const schedulesToInsert = DEMO_DRIVER_SCHEDULES.map(schedule => ({
      ...schedule,
      time_start: schedule.timeStart,
      time_end: schedule.timeEnd,
      land_transporter_id: schedule.landTransporterId,
      company_id: schedule.companyId,
      schedule_date: schedule.scheduleDate,
      is_bulk_copy_source: schedule.isBulkCopySource,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }));

    const { data, error: insertError } = await supabase
      .from('driver_schedules')
      .insert(schedulesToInsert as any[])
      .select();

    if (insertError) {
      throw insertError;
    }

    const loadedCount = data?.length || 0;

    logger.info('✅ デモドライバースケジュール投入完了', {
      loadedCount,
      stats: DEMO_SCHEDULE_STATS,
    });

    return {
      success: true,
      message: `${loadedCount}個のドライバースケジュールを投入しました`,
      loadedCount,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('❌ デモドライバースケジュール投入失敗:', errorMessage);

    return {
      success: false,
      message: 'デモドライバースケジュール投入に失敗しました',
      error: errorMessage,
    };
  }
};

/**
 * デモドライバースケジュールをデータベースから削除
 */
export const clearDemoDriverSchedules = async (): Promise<{
  success: boolean;
  message: string;
  deletedCount?: number;
  error?: string;
}> => {
  try {
    logger.info('🧹 デモドライバースケジュール削除開始');

    const supabase = getSupabaseClient();
    if (!supabase) {
      throw new Error('Supabaseクライアントが初期化されていません');
    }

    const { data, error } = await supabase
      .from('driver_schedules')
      .delete()
      .neq('id', '') // すべて削除
      .select();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    const deletedCount = data?.length || 0;

    logger.info('✅ デモドライバースケジュール削除完了', {
      deletedCount,
    });

    return {
      success: true,
      message: `${deletedCount}個のドライバースケジュールを削除しました`,
      deletedCount,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('❌ デモドライバースケジュール削除失敗:', errorMessage);

    return {
      success: false,
      message: 'デモドライバースケジュール削除に失敗しました',
      error: errorMessage,
    };
  }
};

/**
 * 指定された陸送員のドライバースケジュールを取得
 */
export const getDemoSchedulesByTransporter = async (
  landTransporterId: string
): Promise<DriverSchedule[]> => {
  try {
    const supabase = getSupabaseClient();
    if (!supabase) {
      throw new Error('Supabaseクライアントが初期化されていません');
    }

    const { data, error } = await supabase
      .from('driver_schedules')
      .select('*')
      .eq('land_transporter_id', landTransporterId)
      .order('schedule_date', { ascending: true });

    if (error) {
      throw error;
    }

    return (
      data?.map(schedule => ({
        id: schedule.id,
        companyId: schedule.company_id,
        landTransporterId: schedule.land_transporter_id,
        scheduleDate: schedule.schedule_date,
        timeStart: schedule.time_start,
        timeEnd: schedule.time_end,
        status: schedule.status,
        isBulkCopySource: schedule.is_bulk_copy_source,
        notes: schedule.notes,
        createdAt: schedule.created_at,
        updatedAt: schedule.updated_at,
        createdBy: schedule.created_by,
        updatedBy: schedule.updated_by,
      })) || []
    );
  } catch (error) {
    logger.error('❌ スケジュール取得失敗:', error);
    return [];
  }
};

/**
 * ドライバースケジュール統計を取得
 */
export const getDemoDriverScheduleStats = async (): Promise<{
  totalCount: number;
  confirmedCount: number;
  registeredCount: number;
  cancelledCount: number;
  bulkCopySourceCount: number;
}> => {
  try {
    const supabase = getSupabaseClient();
    if (!supabase) {
      throw new Error('Supabaseクライアントが初期化されていません');
    }

    const { data, error } = await supabase
      .from('driver_schedules')
      .select('status, is_bulk_copy_source', { count: 'exact' });

    if (error) {
      throw error;
    }

    const schedules = data || [];
    return {
      totalCount: schedules.length,
      confirmedCount: schedules.filter(s => s.status === 'confirmed').length,
      registeredCount: schedules.filter(s => s.status === 'registered').length,
      cancelledCount: schedules.filter(s => s.status === 'cancelled').length,
      bulkCopySourceCount: schedules.filter(s => s.is_bulk_copy_source).length,
    };
  } catch (error) {
    logger.error('❌ スケジュール統計取得失敗:', error);
    return {
      totalCount: 0,
      confirmedCount: 0,
      registeredCount: 0,
      cancelledCount: 0,
      bulkCopySourceCount: 0,
    };
  }
};
